import type { JSX, ReactNode, ForwardedRef, ForwardRefExoticComponent, RefAttributes } from 'react';
import type { ToggleGroupSingleProps } from '@radix-ui/react-toggle-group';
import { forwardRef } from 'react';
import { styled, css } from 'styled-components';
import { Root as ToggleGroupRoot } from '@radix-ui/react-toggle-group';
import { isNil } from '@wistia/type-guards';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';
import { getColorScheme } from '../../private/helpers/getColorScheme';
import { SelectedItemStyleProvider } from './useSelectedItemStyle';
import { SelectedItemIndicator } from './SelectedItemIndicator';
import { SegmentedControlValueProvider } from './useSegmentedControlValue';

type SegmentedControlStylesProps = {
  $colorScheme?: ColorSchemeTypes | 'nav';
  $fullWidth?: boolean | undefined;
};

type StyledSegmentedControlProps = SegmentedControlStylesProps & ToggleGroupSingleProps;

export const segmentedControlStyles = css<SegmentedControlStylesProps>`
  ${({ $colorScheme }) => $colorScheme && getColorScheme($colorScheme)};
  ${({ $colorScheme }) =>
    $colorScheme !== 'nav' && `--wui-color-text-selected: var(--wui-color-text-link);`}

  display: inline-flex;
  background-color: var(--wui-color-bg-surface-secondary);
  border-radius: var(--wui-border-radius-rounded);
  gap: var(--wui-space-01);
  isolation: isolate;
  overflow: hidden;
  padding: 2px;
  position: relative;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
`;

const StyledSegmentedControl = styled(ToggleGroupRoot)<StyledSegmentedControlProps>`
  ${segmentedControlStyles}
`;

export type SegmentedControlProps = Omit<ToggleGroupSingleProps, 'type'> & {
  /**
   * `SegmentedControlItem`s that will be rendered in grouping
   */
  children?: ReactNode;
  /**
   * Determines the color scheme for the SegmentedControl and its children.
   */
  colorScheme?: ColorSchemeTypes;
  /**
   * Set the entire `SegmentedControl` to be disabled
   */
  disabled?: boolean | undefined;
  /**
   * Force `SegmentedControl` to fill the width of it's container
   */
  fullWidth?: boolean | undefined;
  /**
   * Callback function invoked when the `SegmentedControlItem` value changes
   */
  onSelectedValueChange: (value: string) => void;
  /**
   * The value of the `SegmentedControlItem` which should be selected
   */
  selectedValue: string;
};

/**
 * Provides a horizontal set of segments for switching between different values
 */
export const SegmentedControl: ForwardRefExoticComponent<
  RefAttributes<HTMLDivElement> & SegmentedControlProps
> = forwardRef(
  (
    {
      children,
      colorScheme = 'inherit',
      disabled = false,
      fullWidth = false,
      selectedValue,
      onSelectedValueChange,
      ...props
    }: SegmentedControlProps,
    ref: ForwardedRef<HTMLDivElement>,
  ): JSX.Element | null => {
    if (isNil(children)) {
      return null;
    }

    return (
      <StyledSegmentedControl
        ref={ref}
        $colorScheme={colorScheme}
        $fullWidth={fullWidth}
        disabled={disabled}
        loop={true}
        onValueChange={onSelectedValueChange}
        rovingFocus={true}
        type="single"
        value={selectedValue}
        {...props}
      >
        <SegmentedControlValueProvider value={selectedValue}>
          <SelectedItemStyleProvider>
            {children}
            <SelectedItemIndicator />
          </SelectedItemStyleProvider>
        </SegmentedControlValueProvider>
      </StyledSegmentedControl>
    );
  },
);

SegmentedControl.displayName = 'SegmentedControl';
